import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, Loader, Form } from "semantic-ui-react";
import { WaterBalanceWorkspace, BalanceSidebar, ModoDatosWorkspace, ModoDia0Workspace } from "../../../components/Admin/WaterBalance";
import { construirDemoATelas } from "../../../components/Admin/WaterBalance/datosDemo";
import { navDeModo, PRIMER_PASO_POR_MODO } from "../../../components/Admin/WaterBalance/navPorModo";
import { useAuth } from "../../../hooks";
import { useSite } from "../../../contexts";
import { isAdmin, isMaster } from "../../../utils/checkPermission";
import { PERIODS } from "../../../utils";
import { BalanceHidricoForm, Waterform, Productionform, Socialform } from "../../../api";
import { useWaterBalanceT } from "../../../components/Admin/WaterBalance/useWaterBalanceT";
import "./WaterBalance.scss";

const balanceCtrl = new BalanceHidricoForm();
const waterCtrl = new Waterform();
const productionCtrl = new Productionform();
const socialCtrl = new Socialform();

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].map((y) => ({ key: y, value: String(y), text: String(y) }));

// Página del módulo Balance Hídrico. La barra lateral (marca + selector de
// modo + pasos) es un solo panel combinado (BalanceSidebar), igual que el
// <aside> de Sustemia_BalanceHidrico_Modulo_ES_v1.html — no dos piezas
// separadas. Acá solo se resuelve: qué modo, qué paso, y para "Mi planta"
// además qué sitio+período+año (porque ese modo sí hereda de Agua,
// Producción y Social; los otros tres no).
export function WaterBalance() {
  const t = useWaterBalanceT();
  const {
    user: { role, site },
  } = useAuth();
  const { accessToken } = useAuth();
  const siteContext = useSite().site;

  const [modo, setModo] = useState("cero");
  const [step, setStep] = useState(PRIMER_PASO_POR_MODO.cero);
  const [siteSelected, setSiteSelected] = useState(null);
  const [year, setYear] = useState(String(currentYear));
  const [period, setPeriod] = useState(PERIODS[0]);

  const [balance, setBalance] = useState(undefined); // undefined = cargando, null = no existe
  const [waterDoc, setWaterDoc] = useState(null);
  const [productionDoc, setProductionDoc] = useState(null);
  const [socialDoc, setSocialDoc] = useState(null);

  const PERIOD_OPTIONS = PERIODS.map((p) => ({ key: p, value: p, text: t(p) }));
  const demo = useMemo(() => construirDemoATelas(), []);

  const cambiarModo = (nuevoModo) => {
    setModo(nuevoModo);
    setStep(PRIMER_PASO_POR_MODO[nuevoModo]);
  };

  useEffect(() => {
    if (siteContext !== "") setSiteSelected(siteContext);
  }, [siteContext]);

  useEffect(() => {
    if (site) setSiteSelected(site);
  }, [site]);

  const cargarTodo = useCallback(async () => {
    if (modo !== "cero" || !siteSelected || !period || !year) {
      setBalance(undefined);
      return;
    }
    setBalance(undefined);
    try {
      const [balanceRes, waterRes, productionRes, socialRes] = await Promise.all([
        balanceCtrl.getBalanceHidricoBySiteAndPeriodAndYear(accessToken, siteSelected, period, year),
        waterCtrl.getWaterFormsBySiteAndYear(accessToken, siteSelected, year),
        productionCtrl.getProductionFormsBySiteAndYear(accessToken, siteSelected, year),
        socialCtrl.getSocialFormsBySiteAndYear(accessToken, siteSelected, year),
      ]);
      setBalance(balanceRes.balance || null);
      const water = (waterRes.waterForms || []).find((w) => w.period === period);
      setWaterDoc(water || null);
      const production = (productionRes.productionForms || productionRes.productions || []).find((p) => p.period === period);
      setProductionDoc(production || null);
      const social = (socialRes.socialForms || []).find((s) => s.period === period);
      setSocialDoc(social || null);
    } catch (error) {
      console.error(error);
      setBalance(null);
    }
  }, [modo, siteSelected, period, year, accessToken]);

  useEffect(() => {
    cargarTodo();
  }, [cargarTodo]);

  const showSiteWarning = siteSelected === null && (isMaster(role) || (isAdmin(role) && !site));

  return (
    <div className="water-balance-page">
      <div className="water-balance-page__wrap">
        <div className="water-balance-page__body">
          <BalanceSidebar modo={modo} onChangeModo={cambiarModo} steps={navDeModo(modo)} activeStep={step} onChangeStep={setStep} />

          <div className="water-balance-page__content">
            {modo === "datos" && <ModoDatosWorkspace step={step} />}
            {modo === "dia0" && <ModoDia0Workspace step={step} />}
            {modo === "demo" && (
              <WaterBalanceWorkspace
                balance={null}
                siteSelected={null}
                period="2025"
                year="2025"
                waterDoc={demo.waterDoc}
                productionDoc={demo.productionDoc}
                socialDoc={demo.socialDoc}
                seedLocal={demo.localSeed}
                esDemo
                step={step}
                onBalanceSaved={() => {}}
              />
            )}

            {modo === "cero" &&
              (showSiteWarning ? (
                <div className="water-balance-page__no-site">
                  <Icon name="warning sign" size="big" />
                  <p>{t("select_company_site_from_header")}</p>
                </div>
              ) : (
                <>
                  <Form className="water-balance-page__period-picker">
                    <Form.Group>
                      <Form.Dropdown label={t("year")} selection options={YEAR_OPTIONS} value={year} onChange={(_, d) => setYear(d.value)} />
                      <Form.Dropdown label={t("period")} selection options={PERIOD_OPTIONS} value={period} onChange={(_, d) => setPeriod(d.value)} />
                    </Form.Group>
                  </Form>

                  {balance === undefined ? (
                    <Loader active inline="centered" />
                  ) : (
                    <WaterBalanceWorkspace
                      balance={balance}
                      siteSelected={siteSelected}
                      period={period}
                      year={year}
                      waterDoc={waterDoc}
                      productionDoc={productionDoc}
                      socialDoc={socialDoc}
                      step={step}
                      onBalanceSaved={(saved) => setBalance(saved)}
                    />
                  )}
                </>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WaterBalance;
